import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';
import { useCurrentUser } from '../contexts/CurrentUserContext';
import AppModal from './AppModal';
import HelpButton from '../tours/HelpButton';
import useChatIntro from '../tours/useChatIntro';
import Recipes from './Recipes';
import HouseholdPolls from './HouseholdPolls';
import { messageFromError } from '../utils/apiError';   // Block 233

const SLOTS = ['breakfast', 'lunch', 'dinner', 'snacks'];
const SLOT_LABEL = { breakfast: 'Breakfast', lunch: 'Lunch', dinner: 'Dinner', snacks: 'Snacks' };
const SLOT_ICON  = { breakfast: '☀️', lunch: '🌤', dinner: '🌙', snacks: '🍎' };

function isoDate(d) {
  return d.toISOString().slice(0, 10);
}

function weekMonday(ref) {
  const d = new Date(ref);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
}

function addDays(d, n) {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function fmtDay(d) {
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}

function fmtDayLong(d) {
  return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
}

function fmtWeekRange(monday) {
  const sunday = addDays(monday, 6);
  const m = monday.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  const s = sunday.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  return `${m} – ${s}`;
}

function isThisWeek(monday) {
  const thisMonday = isoDate(weekMonday(new Date()));
  return isoDate(monday) === thisMonday;
}

const inputStyle = {
  width: '100%', padding: '8px 10px', borderRadius: 8,
  border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box', outline: 'none',
};
const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 3 };
const fieldStyle = { marginBottom: 12 };

export default function MealPlanner() {
  const { activeWorkspaceId, workspaces } = useCurrentUser();
  useChatIntro('mealplanner'); // INTRO-2: no auto-tour here, so fires on first visit
  const isPersonalWs = (workspaces || []).find(w => w.id === activeWorkspaceId)?.type === 'personal';
  const [view, setView] = useState('planner'); // 'planner' | 'recipes' (K2)

  const [monday, setMonday]       = useState(() => weekMonday(new Date()));
  const [entries, setEntries]     = useState([]);   // flat list from API
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');

  // Modal state
  const [modal, setModal]         = useState(null); // { date, slot } or null
  const [form, setForm]           = useState({ title: '', notes: '' });
  const [saving, setSaving]       = useState(false);

  // Mobile: detect narrow viewport
  const [isMobile, setIsMobile]   = useState(() => window.innerWidth < 640);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // K1 — "What can I make?": assemble the household pantry (excluding used-up
  // items) into a comma-separated string and hand it to Aidey Chat, which calls
  // the existing suggest_meals tool. If the pantry is empty we still open chat
  // pantry-first (the tool falls back to the pantry when no ingredients are given).
  const handleWhatCanIMake = useCallback(async () => {
    let names = [];
    try {
      const res = await api('/api/pantry');
      const data = await res.json();
      names = (data.items || []).filter(i => i.status !== 'used_up').map(i => i.name);
    } catch { /* fall through to the pantry-first prompt */ }
    const message = names.length
      ? `What can I make with what's in my pantry: ${names.join(', ')}?`
      : `What can I make from what's in my pantry?`;
    window.dispatchEvent(new CustomEvent('aidey:open-chat', { detail: { message } }));
  }, []);

  const load = useCallback(async () => {
    if (!activeWorkspaceId || !isPersonalWs) return;
    setLoading(true);
    setError('');
    try {
      const res = await api(`/api/meal-plan?week_start=${isoDate(monday)}`);
      setEntries(await res.json());
    } catch (err) {
      setError(messageFromError(err, 'Could not load your meal plan.'));
    } finally {
      setLoading(false);
    }
  }, [monday, activeWorkspaceId, isPersonalWs]);

  useEffect(() => { load(); }, [load]);

  // K4 — reload when a poll winner is dropped into a slot.
  useEffect(() => {
    const onUpdated = () => load();
    window.addEventListener('aidey:mealPlanUpdated', onUpdated);
    return () => window.removeEventListener('aidey:mealPlanUpdated', onUpdated);
  }, [load]);

  function entryFor(date, slot) {
    const key = typeof date === 'string' ? date : isoDate(date);
    return entries.find(e => e.date.slice(0, 10) === key && e.slot === slot) || null;
  }

  function openSlot(date, slot) {
    const existing = entryFor(date, slot);
    setForm({ title: existing?.title || '', notes: existing?.notes || '' });
    setModal({ date: isoDate(date), slot });
  }

  function closeModal() { setModal(null); }

  async function saveSlot() {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const res = await api('/api/meal-plan', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: modal.date, slot: modal.slot, title: form.title.trim(), notes: form.notes || null }),
      });
      await load();
      closeModal();
    } catch (err) {
      setError(messageFromError(err, 'Could not save that meal.'));
    } finally {
      setSaving(false);
    }
  }

  async function clearSlot(date, slot) {
    try {
      await api('/api/meal-plan', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: isoDate(date), slot }),
      });
      await load();
    } catch (err) {
      setError(messageFromError(err, 'Could not clear that slot.'));
    }
  }

  function prevWeek() { setMonday(m => addDays(m, -7)); }
  function nextWeek() { setMonday(m => addDays(m, 7)); }
  function goThisWeek() { setMonday(weekMonday(new Date())); }

  const days = Array.from({ length: 7 }, (_, i) => addDays(monday, i));

  // ── Shared slot cell renderer ────────────────────────────────────────────────
  function SlotCell({ date, slot, compact }) {
    const entry = entryFor(date, slot);
    const [confirmClear, setConfirmClear] = useState(false);
    return (
      <div
        onClick={() => openSlot(date, slot)}
        style={{
          minHeight: compact ? 44 : 56,
          borderRadius: 8,
          padding: '6px 8px',
          background: entry ? 'rgba(228,230,155,0.28)' : '#fafafa',
          border: entry ? '1px solid rgba(228,230,155,0.75)' : '1px dashed #d1d5db',
          cursor: 'pointer',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
        title={entry ? `Edit: ${entry.title}` : `Add ${SLOT_LABEL[slot]}`}
      >
        {entry ? (
          <>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#1a2e3b', lineHeight: 1.3, wordBreak: 'break-word' }}>{entry.title}</span>
            {entry.notes && <span style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{entry.notes}</span>}
            {confirmClear ? (
              <span style={{ position: 'absolute', top: 3, right: 3, display: 'inline-flex', gap: 3 }} onClick={e => e.stopPropagation()}>
                <button onClick={() => { clearSlot(date, slot); setConfirmClear(false); }} style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 4, cursor: 'pointer', color: '#dc2626', fontSize: 11, padding: '1px 5px', fontWeight: 700 }}>Yes</button>
                <button onClick={() => setConfirmClear(false)} style={{ background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer', color: '#555', fontSize: 11, padding: '1px 5px' }}>No</button>
              </span>
            ) : (
              <button
                onClick={e => { e.stopPropagation(); setConfirmClear(true); }}
                style={{
                  position: 'absolute', top: 4, right: 4,
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: 12, color: '#9ca3af', lineHeight: 1, padding: 2,
                }}
                title="Clear"
              >✕</button>
            )}
          </>
        ) : (
          <span style={{ fontSize: 12, color: '#b0b8c4', fontStyle: 'italic' }}>+ Add</span>
        )}
      </div>
    );
  }

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '1rem 1rem 4rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '0 0 0.25rem' }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1a2e3b', margin: 0 }}>Meal Planner</h2>
        <HelpButton tourId="mealplanner" />
      </div>

      {/* K2 — Planner / Recipes view switcher (recipes hang off Meal Planner) */}
      <div style={{ display: 'flex', gap: '0.4rem', margin: '0.5rem 0 1rem' }}>
        {[['planner', '📅 Planner'], ['recipes', '📖 Recipes']].map(([key, label]) => (
          <button key={key} onClick={() => setView(key)}
            style={{
              fontFamily: "'Raleway', sans-serif", fontSize: '0.85rem', fontWeight: 700, padding: '0.4rem 0.9rem',
              borderRadius: 999, cursor: 'pointer',
              background: view === key ? '#1a2e3b' : '#f1f5f9',
              color: view === key ? '#fff' : '#4b5563',
              border: `1px solid ${view === key ? '#1a2e3b' : '#dde3ea'}`,
            }}>
            {label}
          </button>
        ))}
      </div>

      {view === 'recipes' ? <Recipes /> : (<>
      <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 0.75rem' }}>Plan your meals for the week. Ask Aidey "what can I make tonight" or "plan the week from the cupboard".</p>

      {/* K1 — What can I make? (reads the pantry, no typing) */}
      <button
        onClick={handleWhatCanIMake}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: '1rem',
          fontFamily: "'Raleway', sans-serif", fontSize: '0.88rem', fontWeight: 700,
          padding: '0.5rem 1rem', background: '#1a2e3b', color: '#fff',
          border: 'none', borderRadius: 999, cursor: 'pointer',
        }}
      >
        🍳 What can I make?
      </button>

      {/* K4 — family polls */}
      <HouseholdPolls />

      {/* Week navigation */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <button
          onClick={prevWeek}
          style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 14 }}
        >← Prev</button>
        <span style={{ fontWeight: 600, fontSize: 14, color: '#374151', flex: 1, textAlign: 'center', minWidth: 180 }}>
          {fmtWeekRange(monday)}
        </span>
        <button
          onClick={nextWeek}
          style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 14 }}
        >Next →</button>
        {!isThisWeek(monday) && (
          <button
            onClick={goThisWeek}
            style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #3b82f6', background: '#eff6ff', color: '#1d4ed8', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
          >This week</button>
        )}
      </div>

      {error && <p style={{ color: '#b91c1c', marginBottom: 12, fontSize: 13 }}>{error}</p>}
      {!activeWorkspaceId && <p style={{ color: '#bbb', fontSize: 13 }}>Connecting to workspace…</p>}
      {activeWorkspaceId && loading && <p style={{ color: '#6b7280', fontSize: 13 }}>Loading...</p>}

      {!loading && (
        isMobile ? (
          // ── Mobile: stacked days ─────────────────────────────────────────────
          <div data-tour-id="mealplanner-week" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {days.map(day => {
              const isToday = isoDate(day) === isoDate(new Date());
              return (
                <div key={isoDate(day)} style={{
                  borderRadius: 12, border: isToday ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                  overflow: 'hidden', background: '#fff',
                }}>
                  <div style={{
                    background: isToday ? '#eff6ff' : '#f9fafb',
                    padding: '8px 12px',
                    fontWeight: 700, fontSize: 14,
                    color: isToday ? '#1d4ed8' : '#1a2e3b',
                    borderBottom: '1px solid #e5e7eb',
                  }}>
                    {fmtDayLong(day)}{isToday ? ', Today' : ''}
                  </div>
                  <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {SLOTS.map(slot => (
                      <div key={slot}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                          {SLOT_ICON[slot]} {SLOT_LABEL[slot]}
                        </div>
                        <SlotCell date={day} slot={slot} compact />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          // ── Desktop: 7-column grid ───────────────────────────────────────────
          <div data-tour-id="mealplanner-week" style={{ overflowX: 'auto', background: '#fff', borderRadius: 14, boxShadow: '0 2px 14px rgba(0,0,0,0.07)', border: '1px solid #e5e7eb', padding: '10px 8px' }}>
            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '4px 4px', tableLayout: 'fixed' }}>
              <thead>
                <tr>
                  <th style={{ width: 90, padding: '6px 4px', fontSize: 11, fontWeight: 600, color: '#9ca3af', textAlign: 'left' }}></th>
                  {days.map(day => {
                    const isToday = isoDate(day) === isoDate(new Date());
                    return (
                      <th key={isoDate(day)} style={{
                        padding: '6px 4px', textAlign: 'center',
                        fontSize: 12, fontWeight: 700,
                        color: isToday ? '#1d4ed8' : '#1a2e3b',
                        background: isToday ? '#eff6ff' : 'transparent',
                        borderRadius: 6,
                      }}>
                        {fmtDay(day)}
                        {isToday && <div style={{ fontSize: 10, color: '#3b82f6', fontWeight: 600 }}>Today</div>}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {SLOTS.map(slot => (
                  <tr key={slot}>
                    <td style={{ verticalAlign: 'middle', padding: '2px 4px' }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        {SLOT_ICON[slot]}<br />{SLOT_LABEL[slot]}
                      </div>
                    </td>
                    {days.map(day => (
                      <td key={isoDate(day)} style={{ verticalAlign: 'top', padding: '2px 0' }}>
                        <SlotCell date={day} slot={slot} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* Add / Edit modal */}
      <AppModal open={!!modal} onClose={closeModal} closeOnBackdrop ariaLabel="Edit meal" maxWidth={380}>
        {modal && (
          <>
            <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700, color: '#1a2e3b' }}>
              {SLOT_ICON[modal.slot]} {SLOT_LABEL[modal.slot]}
            </h3>
            <p style={{ margin: '0 0 16px', fontSize: 13, color: '#6b7280' }}>
              {fmtDayLong(new Date(modal.date + 'T00:00:00'))}
            </p>
            <div style={fieldStyle}>
              <label style={labelStyle}>Meal</label>
              <input
                autoFocus
                style={inputStyle}
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="e.g. Spaghetti Bolognese"
                onKeyDown={e => { if (e.key === 'Enter') saveSlot(); if (e.key === 'Escape') closeModal(); }}
              />
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Notes (optional)</label>
              <input
                style={inputStyle}
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Prep notes, recipe link..."
              />
            </div>
            {entryFor(modal.date, modal.slot) && (
              <button
                onClick={() => { clearSlot(new Date(modal.date + 'T00:00:00'), modal.slot); closeModal(); }}
                style={{ display: 'block', width: '100%', marginBottom: 8, padding: '9px 0', borderRadius: 10, border: '1px solid #fca5a5', background: '#fff7f7', color: '#b91c1c', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}
              >
                Clear slot
              </button>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={closeModal}
                style={{ flex: 1, padding: '9px 0', borderRadius: 10, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}
              >Cancel</button>
              <button
                onClick={saveSlot}
                disabled={saving || !form.title.trim()}
                style={{ flex: 2, padding: '9px 0', borderRadius: 10, border: 'none', background: form.title.trim() ? '#1a2e3b' : '#e5e7eb', color: form.title.trim() ? '#fff' : '#9ca3af', fontWeight: 700, fontSize: 14, cursor: form.title.trim() ? 'pointer' : 'default' }}
              >{saving ? 'Saving...' : 'Save'}</button>
            </div>
          </>
        )}
      </AppModal>
      </>)}
    </div>
  );
}
